/**
 * DocsApplication - Documents v2 Application
 * 
 * Extends BaseApplication to provide document-specific functionality
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig } from '../../../services/baseApplication/types';
import { DOCS_APP_CONFIG } from './DocsConfig';
import { DocsValidator } from './DocsValidator';
import { DocsRenderer } from './DocsRenderer';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { entityService } from '../../../services/entityService';
import { generateUserColor } from '../../shared/appHelpers';

export class DocsApplication extends BaseApplication {
  // Doc-specific services
  private validator: DocsValidator;
  private renderer: DocsRenderer;
  
  // ✅ collaborationProvider inherited from BaseApplication (protected)
  
  constructor() {
    super();
    console.log('[DocsApplication] Constructor');
    
    this.validator = new DocsValidator();
    this.renderer = new DocsRenderer();
  }
  
  /**
   * Load document from backend
   * ✅ UPDATED: Now loads existing Y.js state
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('[DocsApplication] Loading document:', entityId);
    
    try {
      // ✅ 1. Fetch entity from backend first
      const entity = await entityService.get(entityId);
      
      if (!entity) {
        throw new Error(`Document not found: ${entityId}`);
      }
      
      console.log('[DocsApplication] Entity fetched:', {
        id: entity._id,
        type: entity.entityType,
        hasYjsState: !!entity.skeleton?.yjsState
      });
      
      // 2. Set entity ID and loading phase (skip BaseApplication.initialize() which
      //    creates a Y.Doc and builds indices that are immediately discarded below)
      this.entityId = entityId;
      this.loadingPhase = 'loading-minimal';

      // ✅ 3. Create Y.Doc
      this.ydoc = new Y.Doc();
      
      // ✅ 4. Load base64 state via BaseApplication helper
      if (entity.skeleton?.base64) {
        this.loadBase64State(entity.skeleton.base64);
      }
      
      // 5. Get content fragment
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      
      // 6. Initialize empty if NO existing state
      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        this.initializeEmptyDocument();
      }
      
      // 7. Build indices
      await this.buildCoreIndices();
      
      // ✅ 8. Setup auto-save (HTTP-based persistence)
      this.setupAutoSave();
      
      // ✅ 9. Optional: Setup WebSocket collaboration
      if (!options.offline) {
        await this.setupCollaborationWithDoc(entityId, this.ydoc);
      } else {
        console.log('[DocsApplication] Offline mode - HTTP auto-save only');
      }
      
      console.log('[DocsApplication] ✅ Document loaded', {
        entityId,
        nodes: this.indexManager.size(),
        hasExistingState: !!entity.skeleton?.base64,
        autoSaveEnabled: this.autoSaveEnabled,
        offline: options.offline,
        indexStats: this.indexManager.getStats()
      });
      
    } catch (error) {
      console.error('[DocsApplication] Failed to load:', error);
      throw error;
    }
  }
  
  /**
   * Set up collaboration provider
   */
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    console.log('[DocsApplication] Setting up collaboration with pre-loaded doc');
    
    // ✅ Get current user from auth service
    const authUser = authService.getCurrentUser();
    
    if (!authUser) {
      throw new Error('Not authenticated. Please log in to use collaborative features.');
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    this.collaborationProvider = createCollaborationProvider({
      documentId: `doc:${entityId}`,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc,
    });
    
    // Connect
    this.collaborationProvider.connect();
    
    // Wait for initial sync
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.warn('[DocsApplication] Sync timeout - proceeding anyway');
        resolve();
      }, 5000);
      
      this.collaborationProvider.on('synced', () => {
        clearTimeout(timeout);
        console.log('[DocsApplication] ✅ Document synced');
        resolve();
      });
      
      this.collaborationProvider.on('error', (error: Error) => {
        clearTimeout(timeout);
        console.error('[DocsApplication] Sync error (proceeding anyway):', error);
        resolve();
      });
    });
    
    // ✅ REMOVED: Don't overwrite our pre-loaded ydoc!
    // this.ydoc is already set with loaded content
    // Provider is using it via config.doc parameter
    
    console.log('[DocsApplication] ✅ Collaboration ready with pre-loaded doc');
  }
  
  
  /**
   * Initialize empty document with default paragraph
   */
  private initializeEmptyDocument(): void {
    if (!this.yxml) return;
    
    console.log('[DocsApplication] Initializing empty document with REFACTORED UniversalSchema');
    
    // REFACTORED: Use 2-level structure (paragraph → text)
    const paragraph = new Y.XmlElement('paragraph');
    paragraph.setAttribute('id', this.generateId());
    paragraph.setAttribute('className', '');
    
    // Create text node (direct insertion!)
    const text = new Y.XmlText();
    text.insert(0, 'Start writing...');
    
    // Direct insertion (2-level: paragraph → text)
    paragraph.insert(0, [text]);
    
    // Insert into document
    this.yxml.insert(0, [paragraph]);
    
    console.log('✅ [DocsApplication] Created initial paragraph (2-level structure)');
  }
  
  /**
   * Generate unique ID
   */
  
  /**
   * Get app configuration
   */
  protected getConfig(): AppConfig {
    return DOCS_APP_CONFIG;
  }
  
  /**
   * Validate a node (doc-specific validation)
   */
  validateNode(node: any): { valid: boolean; errors: string[] } {
    return this.validator.validate(node as any);
  }
  
  /**
   * Render a node to DOM
   */
  renderNode(node: any): HTMLElement {
    return this.renderer.render(node as any);
  }
  
  /**
   * Get document statistics
   */
  getDocumentStats(): {
    wordCount: number;
    characterCount: number;
    paragraphCount: number;
    headingCount: number;
    imageCount: number;
  } {
    const stats = {
      wordCount: 0,
      characterCount: 0,
      paragraphCount: 0,
      headingCount: 0,
      imageCount: 0
    };
    
    // Count from indices
    for (const [nodeId, node] of this.byIdIndex) {
      const className = (node as any).getAttribute?.('className');
      
      if (className === 'paragraph') {
        stats.paragraphCount++;
      } else if (className?.startsWith('heading-')) {
        stats.headingCount++;
      } else if (className === 'image') {
        stats.imageCount++;
      }
      
      // Count words and characters
      const text = this.extractTextFromElement(node as any);
      if (text) {
        stats.wordCount += text.split(/\s+/).filter(w => w.length > 0).length;
        stats.characterCount += text.length;
      }
    }
    
    return stats;
  }
  
  /**
   * Get reading time estimate (in minutes)
   */
  getReadingTime(): number {
    const stats = this.getDocumentStats();
    const wordsPerMinute = 200; // Average reading speed
    return Math.ceil(stats.wordCount / wordsPerMinute);
  }
  
  /**
   * Get document outline (headings)
   */
  getDocumentOutline(): Array<{ level: number; text: string; id: string }> {
    const outline: Array<{ level: number; text: string; id: string }> = [];
    
    for (const [nodeId, node] of this.byIdIndex) {
      const className = (node as any).getAttribute?.('className');
      
      if (className?.startsWith('heading-')) {
        const level = parseInt(className.split('-')[1], 10);
        const text = this.extractTextFromElement(node as any);
        
        outline.push({
          level,
          text,
          id: nodeId
        });
      }
    }
    
    return outline;
  }
  
  /**
   * Search for text in document (case-insensitive)
   */
  async searchDocument(query: string): Promise<Array<{ nodeId: string; text: string; matches: number }>> {
    const results: Array<{ nodeId: string; text: string; matches: number }> = [];
    const queryLower = query.toLowerCase();
    
    for (const [nodeId, node] of this.byIdIndex) {
      const text = this.extractTextFromElement(node as any);
      
      if (text) {
        const textLower = text.toLowerCase();
        const matchCount = (textLower.match(new RegExp(queryLower, 'g')) || []).length;
        
        if (matchCount > 0) {
          results.push({
            nodeId,
            text,
            matches: matchCount
          });
        }
      }
    }
    
    return results.sort((a, b) => b.matches - a.matches);
  }
  
  /**
   * Handle entity_write for docs
   * Override to add doc-specific write logic
   */
  async handleEntityWrite(params: any): Promise<any> {
    console.log('[DocsApplication] handleEntityWrite:', params);
    
    // TODO: Implement doc-specific write operations
    // For now, delegate to base
    return super.handleEntityWrite(params);
  }
  
  /**
   * Export document to markdown
   */
  async exportToMarkdown(): Promise<string> {
    let markdown = '';
    
    // Walk through document in order
    if (this.yxml) {
      for (let i = 0; i < this.yxml.length; i++) {
        const child = this.yxml.get(i);
        const className = (child as any).getAttribute?.('className');
        const text = this.extractTextFromElement(child as any);
        
        if (className?.startsWith('heading-')) {
          const level = parseInt(className.split('-')[1], 10);
          markdown += '#'.repeat(level) + ' ' + text + '\n\n';
        } else if (className === 'paragraph') {
          markdown += text + '\n\n';
        } else if (className === 'code-block') {
          markdown += '```\n' + text + '\n```\n\n';
        } else if (className === 'blockquote') {
          markdown += '> ' + text + '\n\n';
        } else if (className === 'horizontal-rule') {
          markdown += '---\n\n';
        }
      }
    }
    
    return markdown;
  }
  
  /**
   * Export document to plain text
   */
  async exportToPlainText(): Promise<string> {
    let text = '';
    
    for (const [nodeId, node] of this.byIdIndex) {
      const nodeText = this.extractTextFromElement(node as any);
      if (nodeText) {
        text += nodeText + '\n';
      }
    }
    
    return text;
  }
  
  /**
   * Get nodes by className (optimized for docs)
   */
  getNodesByClassName(className: string): any[] {
    const nodes: any[] = [];
    const nodeIds = this.byClassIndex.get(className);
    
    if (nodeIds) {
      for (const nodeId of nodeIds) {
        const node = this.byIdIndex.get(nodeId);
        if (node) {
          nodes.push(node as any);
        }
      }
    }
    
    return nodes;
  }
  
  /**
   * Get all callouts
   */
  getAllCallouts(): Array<{ type: string; text: string; id: string }> {
    const callouts: Array<{ type: string; text: string; id: string }> = [];
    
    const calloutTypes = ['callout-info', 'callout-warning', 'callout-success', 'callout-error'];
    
    for (const type of calloutTypes) {
      const nodes = this.getNodesByClassName(type);
      
      for (const node of nodes) {
        callouts.push({
          type,
          text: this.extractTextFromElement(node),
          id: node.getAttribute?.('id')
        });
      }
    }
    
    return callouts;
  }
  
  /**
   * Get all images
   */
  getAllImages(): Array<{ src: string; alt: string; id: string }> {
    const images: Array<{ src: string; alt: string; id: string }> = [];
    const nodes = this.getNodesByClassName('image');
    
    for (const node of nodes) {
      images.push({
        src: node.getAttribute?.('src') || '',
        alt: node.getAttribute?.('alt') || '',
        id: node.getAttribute?.('id') || ''
      });
    }
    
    return images;
  }
  
  /**
   * Get all code blocks
   */
  getAllCodeBlocks(): Array<{ language: string; code: string; id: string }> {
    const codeBlocks: Array<{ language: string; code: string; id: string }> = [];
    const nodes = this.getNodesByClassName('code-block');
    
    for (const node of nodes) {
      codeBlocks.push({
        language: node.getAttribute?.('language') || 'text',
        code: this.extractTextFromElement(node),
        id: node.getAttribute?.('id') || ''
      });
    }
    
    return codeBlocks;
  }
}

